import { useContext } from 'react';
import { ShareInfo } from '@pelckmans/business-components/types/Audiences';
import useThrowAsyncError from '../../../hooks/useThrowAsyncError';
import { useTranslation } from 'react-i18next';
import { deleteUserSlideSet, SlideSet } from '../_api/SlideSetApi';
import SlideSetsContext from '../_contexts/SlideSetsContext';

const DEFAULT_PROVIDER = 'pelckmans';
const BASE_URL = process.env.REACT_APP_DOCEO_TEMPLATE_URL;

export default function useSlideSets() {
  const { slideSets, setSlideSets, fetchSlideSets } = useContext(SlideSetsContext);

  const handleError = useThrowAsyncError();
  const { t } = useTranslation();

  const getReviewState = (slideSet: SlideSet) => {
    if (slideSet.state === 'REVIEW') {
      return (
        <span className="pill pill--warning" role="status">
          {t('MODULES.MATERIAL.COMPONENTS.SLIDE_SETS.REVIEW')}
        </span>
      );
    }
  };

  const deleteSlideSet = async (slideSet: SlideSet) => {
    try {
      await deleteUserSlideSet(slideSet);
    } catch (e) {
      handleError(e);
    }
  };

  const buildDoceoUrl = (
    slideSet: Pick<SlideSet, 'id'> & Partial<Pick<SlideSet, 'versionId' | 'state'>>,
    isEditMode?: boolean,
    eventSubLocation?: string,
  ) => {
    const provider = localStorage.getItem('p-provider') || DEFAULT_PROVIDER;

    const query = new URLSearchParams();
    if (isEditMode) query.set('edit', isEditMode.toString());
    if (eventSubLocation) query.set('origin', eventSubLocation);
    if (slideSet.state !== undefined && slideSet.state !== 'PUBLISHED' && slideSet.versionId)
      query.set('versionId', slideSet.versionId.toString());

    const url = isEditMode === undefined ? BASE_URL : `${BASE_URL}/overview`;
    return `${url.replace(':provider', provider).replace(':id', slideSet.id.toString())}?${query}`;
  };

  const buildNewSlideSetUrl = (moduleId: string, nodeId: string, eventSubLocation: string) => {
    const provider = localStorage.getItem('p-provider') || DEFAULT_PROVIDER;

    const query = new URLSearchParams({
      moduleId,
      nodeId,
      origin: eventSubLocation,
    });

    return `${BASE_URL.replace(':provider', provider).replace(':id', 'new')}?${query}`;
  };

  const buildCopySlideSetUrl = (
    moduleId: string,
    nodeId: string,
    slideSetId: number,
    versionId: number,
    eventSubLocation: string,
  ) => {
    const provider = localStorage.getItem('p-provider') || DEFAULT_PROVIDER;

    const query = new URLSearchParams({
      moduleId,
      nodeId,
      versionId: versionId.toString(),
      origin: eventSubLocation,
    });

    return `${BASE_URL.replace(':provider', provider).replace(':id', slideSetId.toString())}/copy?${query}`;
  };

  const shareSlideSet = (slideSetId: number, shareInfo?: ShareInfo) => {
    const shared = Boolean(shareInfo?.audiences);

    setSlideSets(slideSets =>
      slideSets.map(current => {
        if (current.id !== slideSetId) return current;
        return { ...current, shared };
      }),
    );
  };

  return {
    slideSets,
    fetchSlideSets,
    getReviewState,
    buildDoceoUrl,
    buildNewSlideSetUrl,
    buildCopySlideSetUrl,
    deleteSlideSet,
    shareSlideSet,
    setSlideSets,
  };
}
